/**
 * Training Curriculum Admin Page
 * Dedicated admin interface for assigning optional resources to each fixed curriculum node.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Empty,
  Form,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PagesWrapper from '../../../PagesWrapper';
import countriesService from '../../../services/countriesService';
import trainingCurriculumService from '../../../services/trainingCurriculumService';
import {
  flattenCourseStructureByType,
  TRAINING_CURRICULUM_TYPES,
  trainingCurriculumTypeOrder,
} from '../../AZCourse/Intro/courseStructure';
import styles from './TrainingCurriculum.module.css';

const { Title, Paragraph, Text } = Typography;

const formatCountryLabel = (country) => `${country?.language || ''}${country?.name ? ` · ${country.name}` : ''}`.trim();
const getTreeNodeKey = (curriculumType, nodeKey) => {
  const pathPrefix = curriculumType === TRAINING_CURRICULUM_TYPES.INSTALLER
    ? 'trainingCurriculum.tree.nodes'
    : `trainingCurriculum.treeVariants.${curriculumType}.nodes`;

  return `${pathPrefix}.n${nodeKey.replace(/\./g, '_')}`;
};

const inferCountryLanguageCode = (country) => {
  const value = `${country?.language || ''} ${country?.name || ''}`.trim().toLowerCase();

  if (!value) return null;
  if (value.includes('english') || value.includes('englisch') || value.includes('anglais') || value.includes('engels')) return 'en';
  if (value.includes('deutsch') || value.includes('german') || value.includes('allemand') || value.includes('duits')) return 'de';
  if (value.includes('français') || value.includes('francais') || value.includes('french') || value.includes('frans')) return 'fr';
  if (value.includes('nederlands') || value.includes('dutch') || value.includes('néerlandais') || value.includes('niederländisch')) return 'nl';

  return null;
};

const formatLibraryLabel = (item) => {
  const context = [item.countryLanguage, item.countryName].filter(Boolean).join(' · ');
  return context ? `${item.name} · ${context}` : item.name;
};

const getLocalizedNode = (node, t, curriculumType) => ({
  ...node,
  title: t(`${getTreeNodeKey(curriculumType, node.nodeKey)}.title`, node.title),
  description: t(`${getTreeNodeKey(curriculumType, node.nodeKey)}.description`, node.description),
});

const getAssignmentTags = (assignment, t) => {
  if (!assignment) {
    return [];
  }

  return [
    assignment.trainingProfile ? { key: 'trainingProfile', color: 'blue', label: t('trainingCurriculum.resources.trainingProfile', 'Training Profile'), value: assignment.trainingProfile.name } : null,
    assignment.quizCollection ? { key: 'quizCollection', color: 'gold', label: t('trainingCurriculum.resources.quizCollection', 'Quiz Collection'), value: assignment.quizCollection.name } : null,
    assignment.learningPath ? { key: 'learningPath', color: 'green', label: t('trainingCurriculum.resources.learningPath', 'Learning Path'), value: assignment.learningPath.name } : null,
    assignment.survey ? { key: 'survey', color: 'purple', label: t('trainingCurriculum.resources.survey', 'Survey'), value: assignment.survey.name } : null,
  ].filter(Boolean);
};

/**
 * Admin page for managing fixed-node curriculum assignments by country.
 */
const TrainingCurriculumAdmin = () => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [countries, setCountries] = useState([]);
  const [selectedCurriculumType, setSelectedCurriculumType] = useState(TRAINING_CURRICULUM_TYPES.INSTALLER);
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [libraries, setLibraries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [error, setError] = useState('');

  const selectedCountry = countries.find((entry) => entry.id === selectedCountryId) || null;
  const selectedTreeLanguageCode = inferCountryLanguageCode(selectedCountry) || 'en';
  const treeT = useMemo(() => i18n.getFixedT(selectedTreeLanguageCode), [i18n, selectedTreeLanguageCode]);

  const nodes = useMemo(
    () => flattenCourseStructureByType(selectedCurriculumType).map((node) => getLocalizedNode(node, treeT, selectedCurriculumType)),
    [selectedCurriculumType, treeT]
  );
  const assignmentMap = useMemo(
    () => Object.fromEntries(assignments.map((entry) => [entry.nodeKey, entry])),
    [assignments]
  );
  const countryOptions = countries.map((country) => ({
    value: country.id,
    label: formatCountryLabel(country),
  }));
  const curriculumTypeOptions = trainingCurriculumTypeOrder.map((curriculumType) => ({
    value: curriculumType,
    label: t(`trainingCurriculum.curriculumTypes.${curriculumType}`, curriculumType),
  }));

  const trainingProfileOptions = (libraries?.trainingProfiles || []).map((item) => ({ value: item.id, label: formatLibraryLabel(item) }));
  const quizCollectionOptions = (libraries?.quizCollections || []).map((item) => ({ value: item.id, label: formatLibraryLabel(item) }));
  const learningPathOptions = (libraries?.learningPaths || []).map((item) => ({ value: item.id, label: formatLibraryLabel(item) }));
  const surveyOptions = (libraries?.surveys || []).map((item) => ({ value: item.id, label: formatLibraryLabel(item) }));

  const fetchBootstrapData = async () => {
    setLoading(true);
    setError('');

    try {
      const [countriesResponse, libraryResponse] = await Promise.all([
        countriesService.getAllCountries({ suppressNotification: true }),
        trainingCurriculumService.getAssignmentLibraries({ suppressNotification: true }),
      ]);

      setCountries(countriesResponse || []);
      setLibraries(libraryResponse);

      if (!selectedCountryId && countriesResponse?.length) {
        setSelectedCountryId(countriesResponse[0].id);
      }
    } catch (fetchError) {
      console.error('Failed to load training curriculum admin data:', fetchError);
      setError(t('trainingCurriculum.adminLoadError', 'Failed to load training curriculum administration data.'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async (countryId, curriculumType = selectedCurriculumType) => {
    if (!countryId) {
      setAssignments([]);
      return;
    }

    setAssignmentsLoading(true);
    try {
      const response = await trainingCurriculumService.getAdminAssignments(countryId, curriculumType, { suppressNotification: true });
      setAssignments(response.items || []);
    } catch (fetchError) {
      console.error('Failed to load training curriculum assignments:', fetchError);
      message.error(t('trainingCurriculum.assignmentLoadError', 'Failed to load curriculum assignments.'));
      setAssignments([]);
    } finally {
      setAssignmentsLoading(false);
    }
  };

  useEffect(() => {
    fetchBootstrapData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchAssignments(selectedCountryId, selectedCurriculumType);
  }, [selectedCountryId, selectedCurriculumType]); // eslint-disable-line react-hooks/exhaustive-deps

  const openAssignmentModal = (node) => {
    const assignment = assignmentMap[node.nodeKey];
    setEditingNode(node);
    form.setFieldsValue({
      trainingProfileId: assignment?.trainingProfile?.id ?? null,
      quizCollectionId: assignment?.quizCollection?.id ?? null,
      learningPathId: assignment?.learningPath?.id ?? null,
      surveyId: assignment?.survey?.id ?? null,
    });
  };

  const closeAssignmentModal = () => {
    setEditingNode(null);
    form.resetFields();
  };

  const handleSaveAssignments = async () => {
    if (!editingNode || !selectedCountryId) {
      return;
    }

    const values = form.getFieldsValue();
    const payload = {
      trainingProfileId: values.trainingProfileId ?? null,
      quizCollectionId: values.quizCollectionId ?? null,
      learningPathId: values.learningPathId ?? null,
      surveyId: values.surveyId ?? null,
    };

    const hasAnySelection = Object.values(payload).some(Boolean);
    const existingAssignment = assignmentMap[editingNode.nodeKey];

    setSaving(true);
    try {
      if (!hasAnySelection) {
        if (existingAssignment) {
          await trainingCurriculumService.deleteAssignment(selectedCountryId, selectedCurriculumType, editingNode.nodeKey);
          message.success(t('trainingCurriculum.assignmentsCleared', 'Assignments cleared.'));
        } else {
          message.warning(t('trainingCurriculum.selectResourceWarning', 'Select at least one resource or close the dialog.'));
          return;
        }
      } else {
        await trainingCurriculumService.upsertAssignment(selectedCountryId, selectedCurriculumType, editingNode.nodeKey, payload);
        message.success(t('trainingCurriculum.assignmentsSaved', 'Assignments saved.'));
      }

      closeAssignmentModal();
      fetchAssignments(selectedCountryId);
    } catch (saveError) {
      const errorMessage = saveError.response?.data?.message || t('trainingCurriculum.assignmentSaveError', 'Failed to save assignments.');
      message.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: t('trainingCurriculum.node', 'Node'),
      dataIndex: 'nodeKey',
      key: 'nodeKey',
      width: 110,
      render: (value) => <Text strong>{value}</Text>,
    },
    {
      title: t('trainingCurriculum.type', 'Type'),
      dataIndex: 'nodeType',
      key: 'nodeType',
      width: 120,
      render: (value) => (
        <Tag color={value === 'section' ? 'geekblue' : 'default'}>
          {value === 'section'
            ? t('trainingCurriculum.section', 'Section')
            : t('trainingCurriculum.topic', 'Topic')}
        </Tag>
      ),
    },
    {
      title: t('trainingCurriculum.titleLabel', 'Title'),
      dataIndex: 'title',
      key: 'title',
      render: (_, record) => (
        <div>
          <Text strong>{record.title}</Text>
          <div className={styles.tableSubtext}>{record.description}</div>
        </div>
      ),
    },
    {
      title: t('trainingCurriculum.status', 'Status'),
      key: 'status',
      width: 130,
      render: (_, record) => (
        assignmentMap[record.nodeKey]
          ? <Tag color="success">{t('trainingCurriculum.assigned', 'Assigned')}</Tag>
          : <Tag>{t('trainingCurriculum.unassigned', 'Unassigned')}</Tag>
      ),
    },
    {
      title: t('trainingCurriculum.assignedResources', 'Assigned Resources'),
      key: 'resources',
      render: (_, record) => {
        const tags = getAssignmentTags(assignmentMap[record.nodeKey], t);

        if (!tags.length) {
          return <Text type="secondary">{t('trainingCurriculum.noAssignment', 'No resources assigned')}</Text>;
        }

        return (
          <Space size={[6, 6]} wrap>
            {tags.map((tag) => (
              <Tag key={tag.key} color={tag.color}>{tag.label}: {tag.value}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: t('trainingCurriculum.actions', 'Actions'),
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Button icon={<EditOutlined />} onClick={() => openAssignmentModal(record)}>
          {t('trainingCurriculum.editAssignments', 'Edit Assignments')}
        </Button>
      ),
    },
  ];

  return (
    <PagesWrapper>
      <div className={styles.pageShell}>
        <div className={styles.headerRow}>
          <div>
            <Title level={2} className={styles.pageTitle}>{t('trainingCurriculum.adminTitle', 'Training Curriculum Assignments')}</Title>
            <Paragraph className={styles.pageSubtitle}>
              {t('trainingCurriculum.adminSubtitle', 'Assign optional published resources to each fixed curriculum node for one country and language context.')}
            </Paragraph>
          </div>
          <Button icon={<ReloadOutlined />} onClick={fetchBootstrapData}>
            {t('trainingCurriculum.refresh', 'Refresh')}
          </Button>
        </div>

        {error && <Alert type="error" showIcon message={error} className={styles.alert} />}

        <div className={styles.toolbar}>
          <div className={styles.selectorGroup}>
            <Text strong>{t('trainingCurriculum.selectCountry', 'Country / Language')}</Text>
            <Select
              value={selectedCountryId}
              options={countryOptions}
              onChange={setSelectedCountryId}
              className={styles.countrySelect}
              placeholder={t('trainingCurriculum.selectCountryPlaceholder', 'Select a country and language')}
            />
          </div>
          <div className={styles.selectorGroup}>
            <Text strong>{t('trainingCurriculum.selectCurriculumType', 'Curriculum profile')}</Text>
            <Select
              value={selectedCurriculumType}
              options={curriculumTypeOptions}
              onChange={(value) => {
                setSelectedCurriculumType(value);
                setEditingNode(null);
              }}
              className={styles.countrySelect}
              placeholder={t('trainingCurriculum.selectCurriculumTypePlaceholder', 'Select a curriculum profile')}
            />
          </div>
        </div>

        <Alert
          type="info"
          showIcon
          className={styles.alert}
          message={t('trainingCurriculum.adminHint', 'Clear all selections in a node dialog to remove the assignment entirely. Only published resources can be assigned.')}
        />

        {(loading || assignmentsLoading) ? (
          <div className={styles.loading}><Spin size="large" /></div>
        ) : !countries.length ? (
          <Empty description={t('trainingCurriculum.noCountries', 'No countries are available yet.')} />
        ) : (
          <Table
            rowKey="nodeKey"
            dataSource={nodes}
            columns={columns}
            pagination={false}
            loading={assignmentsLoading}
            className={styles.table}
          />
        )}

        <Modal
          open={!!editingNode}
          onCancel={closeAssignmentModal}
          onOk={handleSaveAssignments}
          confirmLoading={saving}
          okText={t('trainingCurriculum.saveAssignments', 'Save Assignments')}
          title={editingNode ? t('trainingCurriculum.assignmentModalTitle', 'Assignments for {{nodeKey}} · {{title}}', {
            nodeKey: editingNode.nodeKey,
            title: editingNode.title,
          }) : ''}
          destroyOnClose
        >
          {editingNode && (
            <>
              <Paragraph className={styles.modalDescription}>{editingNode.description}</Paragraph>
              <Form form={form} layout="vertical">
                <Form.Item label={t('trainingCurriculum.resources.trainingProfile', 'Training Profile')} name="trainingProfileId">
                  <Select allowClear showSearch options={trainingProfileOptions} optionFilterProp="label" placeholder={t('trainingCurriculum.resourcePlaceholders.trainingProfile', 'Choose a training profile')} />
                </Form.Item>
                <Form.Item label={t('trainingCurriculum.resources.quizCollection', 'Quiz Collection')} name="quizCollectionId">
                  <Select allowClear showSearch options={quizCollectionOptions} optionFilterProp="label" placeholder={t('trainingCurriculum.resourcePlaceholders.quizCollection', 'Choose a quiz collection')} />
                </Form.Item>
                <Form.Item label={t('trainingCurriculum.resources.learningPath', 'Learning Path')} name="learningPathId">
                  <Select allowClear showSearch options={learningPathOptions} optionFilterProp="label" placeholder={t('trainingCurriculum.resourcePlaceholders.learningPath', 'Choose a learning path')} />
                </Form.Item>
                <Form.Item label={t('trainingCurriculum.resources.survey', 'Survey')} name="surveyId">
                  <Select allowClear showSearch options={surveyOptions} optionFilterProp="label" placeholder={t('trainingCurriculum.resourcePlaceholders.survey', 'Choose a survey')} />
                </Form.Item>
              </Form>
            </>
          )}
        </Modal>
      </div>
    </PagesWrapper>
  );
};

export default TrainingCurriculumAdmin;